import { type FocusEvent, useEffect, useRef, useState } from 'react';
import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { Icon } from '../Icon';
import { Input } from '../Input';
import { createDebouncedEmitter } from './debouncedEmitter';

export const SEARCH_DEBOUNCE_MS = 300;

export type SearchButtonInputProps = {
  value?: string;
  /** Fires as the user types, debounced by `SEARCH_DEBOUNCE_MS`; flushed immediately on Enter, clear, and blur. */
  onChange?: (value: string) => void;
  /** Fires on Enter with the current value, for commit-style consumers. */
  onSubmit?: (value: string) => void;
  placeholder?: string;
  keepExpanded?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Root wrapper (table toolbar / map list use this for a stable search region). The input and clear button get `-input` and `-clear` suffixes. */
  'data-testid'?: string;
};

function SearchButtonInput({
  value = '',
  onChange,
  onSubmit,
  placeholder = 'Search',
  keepExpanded = false,
  className,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: SearchButtonInputProps) {
  const [isExpanded, setIsExpanded] = useState(!!value || keepExpanded);
  const [internalValue, setInternalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // The emitter is created once; its emit reads the latest onChange through a ref.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const emitterRef = useRef<ReturnType<typeof createDebouncedEmitter<string>> | null>(null);
  emitterRef.current ??= createDebouncedEmitter<string>(
    (newValue) => onChangeRef.current?.(newValue),
    SEARCH_DEBOUNCE_MS,
  );
  const emitter = emitterRef.current;

  // Sync internal value with external value prop; a pending debounced emit is stale
  // once the controlled value changes underneath it, so drop it.
  useEffect(() => {
    emitter.cancel();
    setInternalValue(value);
    // Expand if there's a value
    if (value) {
      setIsExpanded(true);
    }
  }, [value, emitter]);

  // Focus input when expanded, unless keepExpanded is true
  useEffect(() => {
    if (isExpanded && !keepExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded, keepExpanded]);

  // Cancel a pending debounced emit on unmount
  useEffect(() => {
    return () => {
      emitter.cancel();
    };
  }, [emitter]);

  const handleButtonClick = () => {
    setIsExpanded(true);
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    // Expand on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsExpanded(true);
    }
  };

  const handleButtonFocus = () => {
    // Expand when focused via keyboard navigation
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    emitter.schedule(newValue);
  };

  const handleClear = () => {
    setInternalValue('');
    emitter.flush('');
    inputRef.current?.focus();
  };

  // Apply what was typed when focus leaves the search, from the input or the clear button, before whatever gets
  // focus acts on the results. An empty value is applied on input blur, and the clear button emits its own value.
  const flushOnLeave = (e: FocusEvent<HTMLElement>) => {
    if (internalValue.trim() && !containerRef.current?.contains(e.relatedTarget)) {
      emitter.flushPending();
    }
  };

  const handleInputBlur = (e: FocusEvent<HTMLInputElement>) => {
    flushOnLeave(e);
    // Only collapse if input is empty
    // Use setTimeout to allow tab navigation to complete before collapsing
    if (!internalValue.trim()) {
      // Clear the value now, so a button clicked next acts on the cleared search
      emitter.flush('');
      setTimeout(() => {
        // Only collapse if focus has actually moved outside our component
        const activeElement = document.activeElement;

        if (activeElement && containerRef.current && !containerRef.current.contains(activeElement) && !keepExpanded) {
          setIsExpanded(false);
        }
      }, 0);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Commit on Enter: flush the debounced change and notify submit-style consumers
    if (e.key === 'Enter') {
      emitter.flush(internalValue);
      onSubmit?.(internalValue);
      inputRef.current?.blur();
    }
    // Clear on Escape
    if (e.key === 'Escape') {
      if (internalValue.trim()) {
        handleClear();
      } else {
        if (!keepExpanded) {
          setIsExpanded(false);
        }
      }
    }
  };

  return (
    <div
      ref={containerRef}
      data-testid={dataTestId ? dataTestId : undefined}
      className={cn(
        'relative flex items-center h-6 overflow-hidden transition-[width] duration-300 ease-in-out',
        isExpanded ? 'w-[200px]' : 'w-6',
        className,
      )}
    >
      {/* Button state */}
      <div
        aria-hidden={isExpanded || undefined}
        className={cn(
          'absolute left-0 top-0 transition-opacity duration-200 ease-in-out',
          isExpanded ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}
      >
        <IconButton
          icon="magnifyingGlass"
          variant="secondary"
          size="icon"
          onClick={handleButtonClick}
          onKeyDown={handleButtonKeyDown}
          onFocus={handleButtonFocus}
          tabIndex={isExpanded ? -1 : undefined}
          aria-label="Search"
        />
      </div>

      {/* Input state */}
      <div
        className={cn(
          'absolute left-0 top-0 w-full h-full transition-opacity duration-200 ease-in-out delay-100',
          isExpanded ? 'opacity-100' : 'opacity-0 pointer-events-none delay-0',
        )}
      >
        <div className="relative flex items-center h-full w-full">
          <div className="absolute left-2 pointer-events-none z-10">
            <Icon icon="magnifyingGlass" size={14} className="text-grey-50" />
          </div>
          <Input
            id="search-button-input"
            ref={inputRef}
            type="text"
            value={internalValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder}
            aria-label={ariaLabel}
            data-testid={dataTestId ? `${dataTestId}-input` : undefined}
            tabIndex={isExpanded ? undefined : -1}
            className={cn('w-full h-full', internalValue ? 'pl-7 pr-7' : 'pl-7 pr-2')}
          />
          {internalValue && (
            <button
              type="button"
              onClick={handleClear}
              onBlur={flushOnLeave}
              className="absolute right-1 z-10 flex items-center justify-center w-4 h-4 rounded hover:bg-grey-10 transition-colors focus-ring"
              aria-label="Clear search"
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
            >
              <Icon icon="cross2" size={12} className="text-grey-50" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export { SearchButtonInput };
